import React from 'react';
import {
  FileCheck,
  Shield,
  Clock,
  Link2,
  AlertTriangle,
  BarChart3,
  FileText,
  CheckCircle2,
  Globe,
  Scale,
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const RegulatoryCompliance = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.3 });
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.2 });

  // Audit Trail Features
  const auditFeatures = [
    { text: 'SHA-256 hash chaining', desc: 'Tamper-proof blockchain-style entries' },
    { text: '25+ event types', desc: 'Orders, fills, cancels, risk events' },
    { text: 'Write-ahead logging', desc: 'Crash recovery with WAL' },
    { text: '7-year retention', desc: 'Configurable long-term storage' },
  ];

  // MiFID II Features
  const mifidFeatures = [
    { icon: FileText, text: 'RTS 25 Order Tagging', desc: 'All 8 required flags' },
    { icon: BarChart3, text: 'Order-to-Trade Ratio', desc: '4:1 → 10:1 → 20:1 monitoring' },
    { icon: Clock, text: 'Clock Sync (100μs)', desc: 'PTP IEEE 1588 support' },
    { icon: FileCheck, text: 'Transaction Reports', desc: 'T+1 JSON/CSV export' },
  ];

  // Anti-Manipulation Detection
  const antiManipulation = [
    { name: 'Spoofing', desc: 'Cancel ratio >80%', dotClass: 'bg-red-400' },
    { name: 'Layering', desc: '5+ price levels', dotClass: 'bg-orange-400' },
    { name: 'Quote Stuffing', desc: '>1000 msg/sec', dotClass: 'bg-yellow-400' },
    { name: 'Wash Trading', desc: 'Self-match detect', dotClass: 'bg-purple-400' },
    { name: 'Momentum Ignition', desc: 'Price move triggers', dotClass: 'bg-blue-400' },
  ];

  // Supported regulatory frameworks (not claiming compliance, but feature support)
  const supportedFrameworks = [
    { text: 'Supports MiFID II', region: 'EU', bgClass: 'bg-blue-500/10', borderClass: 'border-blue-500/20', iconClass: 'text-blue-400' },
    { text: 'SEC 17a-4 Features', region: 'US', bgClass: 'bg-green-500/10', borderClass: 'border-green-500/20', iconClass: 'text-green-400' },
    { text: 'FINRA 4511 Audit', region: 'US', bgClass: 'bg-green-500/10', borderClass: 'border-green-500/20', iconClass: 'text-green-400' },
    { text: 'SEBI Compatible', region: 'India', bgClass: 'bg-orange-500/10', borderClass: 'border-orange-500/20', iconClass: 'text-orange-400' },
  ];

  return (
    <section id="compliance" className="py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
            <Scale className="w-5 h-5 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">Built-in Compliance Features</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Regulatory <span className="text-gradient">Support</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Features to facilitate your compliance efforts — included in every license
          </p>
        </div>

        <div
          ref={contentRef}
          className={`transition-all duration-700 ${
            contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Supported Regulatory Frameworks */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {supportedFrameworks.map((framework, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${framework.bgClass} border ${framework.borderClass}`}
              >
                <Globe className={`w-4 h-4 ${framework.iconClass}`} />
                <span className="text-sm font-semibold text-white">{framework.text}</span>
                <span className={`text-xs ${framework.iconClass}`}>({framework.region})</span>
              </div>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Tamper-Proof Audit Trail */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Link2 className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Tamper-Proof Audit Trail</h3>
                  <p className="text-sm text-muted-foreground">Blockchain-style SHA-256 chaining</p>
                </div>
              </div>

              {/* Visual Chain */}
              <div className="flex items-center justify-center gap-1 mb-6 p-4 rounded-xl bg-black/30">
                {['Entry 1', 'Entry 2', 'Entry 3', '...', 'Entry N'].map((entry, i) => (
                  <React.Fragment key={i}>
                    <div className="px-2 py-1 rounded bg-green-500/20 border border-green-500/30 text-xs text-green-400 font-mono">
                      {entry}
                    </div>
                    {i < 4 && <span className="text-green-400">→</span>}
                  </React.Fragment>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {auditFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">{feature.text}</p>
                      <p className="text-xs text-muted-foreground">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* MiFID II Compliance */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <FileCheck className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">MiFID II RTS 25 Features</h3>
                  <p className="text-sm text-muted-foreground">Built-in support for EU regulatory requirements</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {mifidFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10"
                  >
                    <feature.icon className="w-5 h-5 text-blue-400 mb-2" />
                    <p className="text-sm font-medium text-white">{feature.text}</p>
                    <p className="text-xs text-muted-foreground">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Anti-Manipulation Detection */}
          <div className="max-w-4xl mx-auto mt-8">
            <div className="p-6 rounded-2xl bg-gradient-to-r from-red-500/10 via-orange-500/10 to-yellow-500/10 border border-red-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Real-Time Anti-Manipulation Detection</h3>
                  <p className="text-sm text-muted-foreground">5 detection algorithms with configurable thresholds</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {antiManipulation.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/10"
                  >
                    <div className={`w-2 h-2 rounded-full ${item.dotClass}`} />
                    <span className="text-sm font-medium text-white">{item.name}</span>
                    <span className="text-xs text-muted-foreground">({item.desc})</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 rounded-lg bg-black/30 text-center">
                <p className="text-sm text-muted-foreground">
                  Alerts: <span className="text-white font-medium">Log</span> → <span className="text-yellow-400 font-medium">Flag</span> → <span className="text-orange-400 font-medium">Review</span> → <span className="text-red-400 font-medium">Block Trading</span>
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Note */}
          <div className="text-center mt-10">
            <p className="text-sm text-muted-foreground mb-4">
              Features that facilitate your compliance efforts — included in every ASTRA license.
              <br />
              <span className="text-xs italic mt-1 block">Note: Compliance with specific regulations is your firm's responsibility. ASTRA provides tools to support your efforts.</span>
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
            >
              <FileCheck className="w-4 h-4" />
              Request Compliance Documentation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegulatoryCompliance;
